import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';

/**
 * FeaturesPage - Features overview page
 * Replicates the design of public/features.html
 */
export function FeaturesPage() {
  const features = [
    {
      icon: '📊',
      title: 'One-Click Excel Export',
      description:
        'Download fully functional, unlocked .xlsx files. All formulas are preserved, so you can continue editing offline or share with investors.',
      color: 'blue',
    },
    {
      icon: '⚖️',
      title: '100% Balanced Statements',
      description:
        'Our double-entry accounting engine ensures your Income Statement, Balance Sheet, and Cash Flow always tie out perfectly.',
      color: 'green',
    },
    {
      icon: '🧠',
      title: 'AI-Driven Insights',
      description:
        'Get automated analysis of your metrics. Our AI highlights key risks, growth opportunities, and benchmarks against industry standards.',
      color: 'purple',
    },
    {
      icon: '📈',
      title: 'Industry-Specific Models',
      description:
        'Start with logic that fits your business. SaaS, E-commerce, Marketplace, and more—each with tailored drivers and KPIs.',
      color: 'amber',
    },
    {
      icon: '🎯',
      title: 'Investor-Ready Charts',
      description:
        'Beautiful, automatically generated charts and graphs that you can drop directly into your pitch deck.',
      color: 'red',
    },
    {
      icon: '🔒',
      title: 'Secure & Private',
      description:
        'Your data is yours. We use bank-grade encryption and never share your financial data with third parties.',
      color: 'indigo',
    },
  ];

  const additionalFeatures = [
    {
      title: 'Scenario Analysis',
      description: 'Compare base, optimistic, and pessimistic scenarios side-by-side.',
    },
    {
      title: 'Sensitivity Tables',
      description: 'Understand how key drivers impact your bottom line.',
    },
    {
      title: 'Valuation Models',
      description: 'DCF, revenue multiples, and comparable company analysis.',
    },
    {
      title: 'Working Capital Modeling',
      description: 'AR, AP, inventory, and cash cycle optimization.',
    },
    {
      title: 'Debt Scheduling',
      description: 'Interest calculations and amortization schedules.',
    },
    {
      title: 'Depreciation Tracking',
      description: 'CapEx planning with detailed depreciation schedules.',
    },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans antialiased">
      <Navbar activeLink="features" />

      {/* Header / Hero */}
      <section className="bg-slate-50 py-20 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Powerful Features for Modern Modeling
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Stop struggling with broken spreadsheets. FinModel gives you
              enterprise-grade financial tools, simplified.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Features Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="p-8 rounded-2xl bg-white border border-slate-100 shadow-lg hover:shadow-xl transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div
                  className={`w-14 h-14 bg-${feature.color}-50 rounded-xl flex items-center justify-center text-3xl mb-6`}
                >
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              And Much More...
            </h2>
            <p className="text-lg text-slate-600">
              Everything you need to build professional financial models.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="bg-white p-6 rounded-xl border border-slate-200 hover:border-primary-300 hover:shadow-md transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <h3 className="font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-6">
              Ready to build your model?
            </h2>
            <p className="text-slate-300 text-lg mb-8">
              Join thousands of founders who trust FinModel to plan their
              financial future.
            </p>
            <Link
              to="/dashboard"
              className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-bold py-4 px-10 rounded-lg transition-all shadow-lg hover:shadow-primary-600/30 transform hover:-translate-y-1"
            >
              Start Building for Free
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
